import React from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography } from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import { SectionTab } from './tab-button';
import { t } from '../config';

// ── Line definitions ────────────────────────────────────────────────
const LINES = [
  { name: 'Solid', endHead: '', dash: [], svgLine: { strokeDasharray: 'none' } },
  { name: 'Dashed', endHead: '', dash: [10, 5], svgLine: { strokeDasharray: '8 4' } },
  { name: 'Dotted', endHead: '', dash: [2, 4], svgLine: { strokeDasharray: '2 4' } },
  { name: 'Arrow', endHead: 'arrow', dash: [], svgLine: { strokeDasharray: 'none', marker: true } },
  { name: 'Dot Arrow', endHead: 'arrow', startHead: 'circle', dash: [], svgLine: { strokeDasharray: 'none', marker: true, startDot: true } },
  { name: 'Dash Arrow', endHead: 'arrow', startHead: 'square', dash: [8, 4], svgLine: { strokeDasharray: '6 3', marker: true, startSquare: true } },
];

// ── Shape definitions (100×100 viewBox paths) ───────────────────────
const SHAPES = [
  // Row 1 — basic
  { name: 'Rectangle', subType: 'rect' },
  { name: 'Circle', subType: 'circle' },
  { name: 'Star', subType: 'star' },
  { name: 'Triangle', subType: 'triangle' },
  // Row 2
  { name: 'Parallelogram', subType: 'path', path: 'M20 90 L0 10 L80 10 L100 90 Z' },
  { name: 'Diamond', subType: 'diamond' },
  { name: 'Pentagon', subType: 'polygon' },
  { name: 'Hexagon', subType: 'hexagon' },
  // Row 3
  { name: 'Speech Bubble', subType: 'path', path: 'M10 5 H90 Q95 5 95 10 V55 Q95 60 90 60 H35 L15 80 L25 60 H10 Q5 60 5 55 V10 Q5 5 10 5 Z' },
  { name: 'Cross', subType: 'path', path: 'M35 0 H65 V35 H100 V65 H65 V100 H35 V65 H0 V35 H35 Z' },
  { name: 'Egg', subType: 'path', path: 'M50 5 C75 5 95 30 95 55 C95 80 75 95 50 95 C25 95 5 80 5 55 C5 30 25 5 50 5 Z' },
  { name: 'Cloud', subType: 'path', path: 'M25 80 C10 80 0 70 5 60 C0 50 10 40 22 42 C20 30 30 20 45 20 C55 15 70 20 75 32 C85 28 100 38 95 52 C105 62 95 78 80 78 Z' },
  // Row 4 — arrows
  { name: 'Right Arrow', subType: 'path', path: 'M0 30 H60 V10 L100 50 L60 90 V70 H0 Z' },
  { name: 'Left Arrow', subType: 'path', path: 'M100 30 H40 V10 L0 50 L40 90 V70 H100 Z' },
  { name: 'Down Arrow', subType: 'path', path: 'M30 0 V60 H10 L50 100 L90 60 H70 V0 Z' },
  { name: 'Up Arrow', subType: 'path', path: 'M30 100 V40 H10 L50 0 L90 40 H70 V100 Z' },
  // Row 5 — decorative
  { name: 'Flower 5', subType: 'path', path: 'M50 10 C60 0 75 5 70 20 C85 15 90 30 80 40 C95 45 90 65 75 60 C80 75 65 85 55 70 C50 85 35 80 35 65 C20 75 10 65 20 55 C5 50 10 35 25 35 C15 20 25 10 40 15 C35 0 45 0 50 10 Z' },
  { name: 'Asterisk', subType: 'path', path: 'M50 0 L58 35 L90 15 L65 42 L100 50 L65 58 L90 85 L58 65 L50 100 L42 65 L10 85 L35 58 L0 50 L35 42 L10 15 L42 35 Z' },
  { name: 'Bowtie', subType: 'path', path: 'M0 0 L45 40 L45 0 L55 0 L55 40 L100 0 L100 100 L55 60 L55 100 L45 100 L45 60 L0 100 Z' },
  { name: 'Half Circle', subType: 'path', path: 'M50 0 A50 50 0 0 1 50 100 Z' },
  // Row 6
  { name: 'Rounded Square', subType: 'path', path: 'M20 5 H80 Q95 5 95 20 V80 Q95 95 80 95 H20 Q5 95 5 80 V20 Q5 5 20 5 Z' },
  { name: 'Gem', subType: 'path', path: 'M50 0 L95 35 L50 100 L5 35 Z' },
  { name: 'Arch', subType: 'path', path: 'M5 95 V40 Q5 5 50 5 Q95 5 95 40 V95 H70 V45 Q70 25 50 25 Q30 25 30 45 V95 Z' },
  { name: 'Teardrop', subType: 'path', path: 'M50 5 C70 5 90 25 90 50 C90 75 70 95 50 95 C30 95 10 75 10 50 C10 25 30 5 50 5 Z' },
  // Row 7
  { name: 'Drop', subType: 'path', path: 'M50 0 C55 20 90 40 90 65 C90 85 72 100 50 100 C28 100 10 85 10 65 C10 40 45 20 50 0 Z' },
  { name: 'Starburst', subType: 'path', path: 'M50 0 L56 30 L80 8 L62 34 L100 25 L70 44 L100 50 L70 56 L100 75 L62 66 L80 92 L56 70 L50 100 L44 70 L20 92 L38 66 L0 75 L30 56 L0 50 L30 44 L0 25 L38 34 L20 8 L44 30 Z' },
  { name: 'Wave', subType: 'path', path: 'M0 20 C25 0 25 40 50 20 C75 0 75 40 100 20 V80 C75 100 75 60 50 80 C25 100 25 60 0 80 Z' },
  { name: 'Flower 6', subType: 'path', path: 'M50 5 C55 20 65 15 68 25 C78 18 82 28 75 35 C85 35 88 48 78 50 C88 52 85 65 75 65 C82 72 78 82 68 75 C65 85 55 80 50 95 C45 80 35 85 32 75 C22 82 18 72 25 65 C15 65 12 52 22 50 C12 48 15 35 25 35 C18 28 22 18 32 25 C35 15 45 20 50 5 Z' },
  // Row 8
  { name: 'Hourglass', subType: 'path', path: 'M10 0 H90 L55 45 L55 55 L90 100 H10 L45 55 L45 45 Z' },
  { name: 'Heart', subType: 'path', path: 'M50 90 C25 70 0 55 0 30 C0 10 15 0 30 0 C40 0 48 5 50 15 C52 5 60 0 70 0 C85 0 100 10 100 30 C100 55 75 70 50 90 Z' },
  { name: 'House', subType: 'path', path: 'M50 5 L95 45 V95 H5 V45 Z' },
  { name: 'Arrow Up', subType: 'path', path: 'M50 0 L90 35 H65 V100 H35 V35 H10 Z' },
  // Row 9
  { name: 'Hourglass V', subType: 'path', path: 'M0 0 L100 0 L55 45 L55 55 L100 100 L0 100 L45 55 L45 45 Z' },
  { name: 'Barn', subType: 'path', path: 'M50 0 L95 30 V95 H5 V30 Z' },
  { name: 'Mushroom', subType: 'path', path: 'M5 55 C5 20 30 0 50 0 C70 0 95 20 95 55 H65 V95 H35 V55 Z' },
  { name: 'Lips', subType: 'path', path: 'M10 50 C10 25 30 15 50 25 C70 15 90 25 90 50 C90 75 70 85 50 75 C30 85 10 75 10 50 Z' },
  // Row 10
  { name: 'Leaf', subType: 'path', path: 'M10 90 Q10 10 90 10 Q90 90 10 90 Z' },
  { name: 'Lightning', subType: 'path', path: 'M60 0 L25 45 H50 L15 100 L80 50 H55 L85 0 Z' },
  { name: 'Bolt', subType: 'path', path: 'M55 0 L20 42 H45 L10 100 L80 55 H52 L90 0 Z' },
  { name: 'D Shape', subType: 'path', path: 'M10 5 H60 C90 5 95 30 95 50 C95 70 90 95 60 95 H10 Z' },
  // Row 11
  { name: 'Peanut', subType: 'path', path: 'M35 10 C55 0 80 10 80 30 C95 40 95 60 80 70 C80 90 55 100 35 90 C20 100 5 90 5 70 C-5 60 -5 40 5 30 C5 10 20 0 35 10 Z' },
  { name: 'L Shape', subType: 'path', path: 'M10 0 H50 V60 H90 V100 H10 Z' },
  { name: 'Sun', subType: 'path', path: 'M50 0 L54 35 L75 8 L58 37 L95 20 L63 42 L100 40 L65 48 L98 62 L62 54 L85 80 L56 58 L68 95 L52 62 L50 100 L48 62 L32 95 L44 58 L15 80 L38 54 L2 62 L35 48 L0 40 L37 42 L5 20 L42 37 L25 8 L46 35 Z' },
  { name: 'Concave Rect', subType: 'path', path: 'M0 0 C35 15 65 15 100 0 V100 C65 85 35 85 0 100 Z' },
  // Row 12
  { name: 'Hexagon 2', subType: 'path', path: 'M25 5 H75 L95 50 L75 95 H25 L5 50 Z' },
  { name: 'U Shape', subType: 'path', path: 'M5 0 H30 V65 C30 80 42 90 50 90 C58 90 70 80 70 65 V0 H95 V65 C95 90 75 100 50 100 C25 100 5 90 5 65 Z' },
  { name: 'Corner', subType: 'path', path: 'M0 0 H55 V55 H100 V100 H0 Z' },
  { name: 'T Shape', subType: 'path', path: 'M0 0 H100 V40 H65 V100 H35 V40 H0 Z' },
  // Row 13
  { name: 'Shield', subType: 'path', path: 'M50 95 C20 80 5 60 5 30 V10 H95 V30 C95 60 80 80 50 95 Z' },
  { name: 'Shield 2', subType: 'path', path: 'M50 95 C15 75 5 55 5 25 V5 H95 V25 C95 55 85 75 50 95 Z' },
  { name: 'Parallelogram 2', subType: 'path', path: 'M20 0 H100 L80 100 H0 Z' },
  { name: 'Clover', subType: 'path', path: 'M50 40 C40 30 25 20 25 35 C15 25 0 30 10 45 C0 50 5 65 20 60 C15 75 25 85 40 75 L50 95 L60 75 C75 85 85 75 80 60 C95 65 100 50 90 45 C100 30 85 25 75 35 C75 20 60 30 50 40 Z' },
  // Row 14
  { name: '4-Point Star', subType: 'path', path: 'M50 0 L58 38 L100 50 L58 62 L50 100 L42 62 L0 50 L42 38 Z' },
  { name: 'L Step', subType: 'path', path: 'M0 0 H50 V50 H100 V100 H0 Z' },
  { name: 'Steps', subType: 'path', path: 'M0 0 H35 V35 H65 V65 H100 V100 H0 Z' },
  { name: 'Steps 2', subType: 'path', path: 'M0 0 H30 V30 H50 V0 H80 V50 H100 V100 H0 Z' },
  // Row 15 — organic blobs
  { name: 'Blob 1', subType: 'path', path: 'M60 10 C85 10 95 30 90 50 C95 75 80 95 55 90 C30 95 10 80 10 55 C5 30 30 10 60 10 Z' },
  { name: 'Blob 2', subType: 'path', path: 'M55 5 C80 5 100 25 95 50 C100 75 80 95 55 95 C30 100 5 80 10 55 C0 30 25 5 55 5 Z' },
  { name: 'Blob 3', subType: 'path', path: 'M50 5 C75 0 100 20 95 45 C100 70 80 100 50 95 C20 100 0 75 5 50 C0 25 25 5 50 5 Z' },
  { name: 'Blob 4', subType: 'path', path: 'M60 5 C90 10 100 35 85 60 C95 85 70 100 45 95 C20 95 0 70 10 45 C5 20 30 0 60 5 Z' },
];

// ── SVG thumbnail for shapes ────────────────────────────────────────
const FILL_COLOR = '#BFBFBF';

// Built-in shape → SVG path mapping for thumbnails
const builtInPaths = {
  rect: 'M5 5 H95 V95 H5 Z',
  circle: 'M50 5 A45 45 0 1 1 50 95 A45 45 0 1 1 50 5 Z',
  star: 'M50 5 L62 38 L97 38 L68 60 L79 93 L50 73 L21 93 L32 60 L3 38 L38 38 Z',
  triangle: 'M50 5 L95 90 H5 Z',
  diamond: 'M50 5 L95 50 L50 95 L5 50 Z',
  hexagon: 'M25 10 H75 L95 50 L75 90 H25 L5 50 Z',
  polygon: 'M50 5 L95 38 L80 90 H20 L5 38 Z',
};

const ShapeThumbnail = ({ shape }) => {
  const svgPath = shape.path || builtInPaths[shape.subType] || builtInPaths.rect;
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ display: 'block' }}>
      <path d={svgPath} fill={FILL_COLOR} />
    </svg>
  );
};

// ── Line thumbnail ──────────────────────────────────────────────────
const LineThumbnail = ({ line }) => {
  const s = line.svgLine || {};
  return (
    <svg viewBox="0 0 80 20" width="100%" height="20" style={{ display: 'block' }}>
      {s.startDot && <circle cx="6" cy="10" r="4" fill={FILL_COLOR} />}
      {s.startSquare && <rect x="2" y="6" width="8" height="8" fill={FILL_COLOR} />}
      <line
        x1={s.startDot || s.startSquare ? 12 : 2}
        y1="10"
        x2={s.marker ? 65 : 78}
        y2="10"
        stroke={FILL_COLOR}
        strokeWidth="2.5"
        strokeDasharray={s.strokeDasharray === 'none' ? undefined : s.strokeDasharray}
        strokeLinecap="round"
      />
      {s.marker && (
        <polygon points="62,4 78,10 62,16" fill={FILL_COLOR} />
      )}
    </svg>
  );
};

// ── Panel ───────────────────────────────────────────────────────────
const ElementsPanelContent = observer(({ store }) => {
  const addShape = (shape) => {
    const page = store.activePage;
    if (!page) return;

    const size = Math.min(store.width, store.height) * 0.2;
    const isRect = shape.subType === 'rect';
    const w = isRect ? size * 1.5 : size;
    const h = isRect ? size * 0.8 : size;

    const props = {
      type: 'figure',
      subType: shape.subType,
      x: store.width / 2 - w / 2,
      y: store.height / 2 - h / 2,
      width: w,
      height: h,
      fill: FILL_COLOR,
      stroke: '',
      strokeWidth: 0,
    };
    if (shape.path) {
      props.pathData = shape.path;
    }
    page.addElement(props);
    store.history.addUndoState();
  };

  const addLine = (line) => {
    const page = store.activePage;
    if (!page) return;

    const lineWidth = Math.min(store.width, store.height) * 0.4;
    page.addElement({
      type: 'line',
      x: store.width / 2 - lineWidth / 2,
      y: store.height / 2,
      width: lineWidth,
      height: 4,
      color: '#000000',
      endHead: line.endHead || '',
      startHead: line.startHead || '',
      dash: line.dash || [],
    });
    store.history.addUndoState();
  };

  const shapeBoxSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    aspectRatio: '1',
    borderRadius: '8px',
    cursor: 'pointer',
    padding: '10px',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.06)',
    },
    transition: 'background 0.15s ease',
  };

  const lineBoxSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    cursor: 'pointer',
    padding: '12px 8px',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.06)',
    },
    transition: 'background 0.15s ease',
  };

  return (
    <Box sx={{ p: 1.5, overflowY: 'auto' }}>
      {/* Lines */}
      <Typography variant="subtitle2" sx={{ mb: 1, color: '#555', fontSize: '13px' }}>
        {t('sidePanel.lines', 'Lines')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '4px',
          mb: 2.5,
        }}
      >
        {LINES.map((line, idx) => (
          <Box key={idx} onClick={() => addLine(line)} sx={lineBoxSx}>
            <LineThumbnail line={line} />
          </Box>
        ))}
      </Box>

      {/* Shapes */}
      <Typography variant="subtitle2" sx={{ mb: 1, color: '#555', fontSize: '13px' }}>
        {t('sidePanel.shapes', 'Shapes')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '4px',
        }}
      >
        {SHAPES.map((shape, idx) => (
          <Box key={idx} onClick={() => addShape(shape)} sx={shapeBoxSx}>
            <ShapeThumbnail shape={shape} />
          </Box>
        ))}
      </Box>
    </Box>
  );
});

export const ElementsSection = {
  name: 'elements',
  Tab: (props) => (
    <SectionTab name={t('sidePanel.elements', 'Elements')} {...props}>
      <CategoryIcon />
    </SectionTab>
  ),
  Panel: ElementsPanelContent,
};
